/**
 * WHAT TO ASK ON *THIS* PAGE — the channel from a page to the Ask AI launcher
 * in the bottom-right corner, plus the global fallback list the launcher shows
 * when the page says nothing.
 *
 * ── WHY A PAGE PUSHES INSTEAD OF A CENTRAL ROUTE MAP ──────────────────────
 * The obvious build is `{ '/orders': [...], '/invoicing': [...] }` next to
 * `useLocation()`. We deliberately did not: a map in one file and the pages it
 * describes in twenty-nine others drift the moment a screen changes what it is
 * for, and nothing fails when they do — the launcher just quietly offers the
 * wrong thing, which reads as "it doesn't understand the app". A page is also
 * the only place that knows the things worth putting in a prompt: the day the
 * timeline is actually showing, the invoicing tab in front of you. `useLocation`
 * can only ever tell the corner the URL.
 *
 * This is the same conclusion `src/Pages/Portal/Dispatcher/askSubject.jsx`
 * reached for the dispatcher portal's own launcher, and this module is that
 * mechanism generalised from one portal's two screens to the whole dashboard.
 * The portal's version cannot simply be reused: ChatWidget is deliberately not
 * mounted there (see PortalShell), so the two launchers have no common frame.
 * If you change the semantics here, read that file — they should stay the same
 * idea, and the reasons written there are the reasons here.
 *
 * ── THE CONTRACT ──────────────────────────────────────────────────────────
 * WHAT A PAGE MAY REGISTER: one suggestion, or nothing. Either a bare string,
 *   or `{ question, prompt?, label? }`.
 *     question — what the button in the launcher READS. Keep it short enough
 *                to fit on two lines in a 272px card.
 *     prompt   — what is actually SENT to the assistant. Defaults to
 *                `question`. Split the two when the good prompt is too long to
 *                read as a button, which is the normal case — see
 *                `correspondencePrompt` in AskAIButton.jsx, the per-row
 *                precedent this generalises; a page prompt should be written in
 *                the same voice, and must not become a second vocabulary.
 *     label    — optional replacement for the pill's own "Ask AI" text. This is
 *                the one part of the feature that works with no hover and no
 *                tap, which is why it exists: on touch it is the whole hint.
 *
 * WHEN: any time, and as often as it likes. The hook keys on the suggestion's
 *   VALUE, not its identity, so a page may build the object inline from what is
 *   on screen (`Which of ${day}'s trips…`) without re-registering every render.
 *   Pass a falsy value to register nothing — which is the right answer while the
 *   page's data is still loading, and makes lazy registration the default rather
 *   than a special case. Nothing registered -> the launcher falls back to
 *   GLOBAL_SUGGESTIONS, which is a complete, shipped answer, not a degraded one.
 *
 * IF TWO THINGS REGISTER: the most recently registered one that is still
 *   mounted wins, and when it unmounts the previous one takes over again. The
 *   registrations are a stack, not a slot, so a drawer or modal that declares
 *   something more specific than its page does not leave the page mute after it
 *   closes. One caveat worth knowing before you rely on it: React runs child
 *   effects before parent effects, so a page and a component *inside* it that
 *   both register in the SAME commit resolve to the page. The supported shape is
 *   one registration per page plus, if genuinely needed, a surface that appears
 *   later and wins by arriving later. Two unconditional registrations in one
 *   tree is a design error — a page has one most-relevant question.
 *
 * ON UNMOUNT AND ROUTE CHANGE: a registration removes only its own entry, by
 *   identity. It must be that and not "clear the slot": React can mount the
 *   arriving route before the leaving one has run its cleanup, so a blind reset
 *   would wipe what the new page had just registered. (That exact bug is
 *   written up in askSubject.jsx, where it was found.)
 *
 * WITHOUT A PROVIDER the hook is inert and returns nothing. That is load-bearing,
 *   not sloppiness: pages are rendered bare in their own Jest tests
 *   (OrdersUrlState, InvoicingPage, Orders.integration, …), and registering a
 *   suggestion must never be a reason one of those needs a new wrapper.
 */
import React, {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';

/**
 * The questions the launcher offers when the page has registered nothing, and
 * the ones ChatComposer typewrites into the empty composer. ONE list, because
 * they are the same claim about what the assistant is good for, and two copies
 * would answer "what can it do?" two different ways.
 *
 * Keep under ~50 chars so they fit without wrapping in the compact card.
 */
export const GLOBAL_SUGGESTIONS = [
  "Show me today's bookings",
  'Pricing BUD → Deák Ferenc tér in EUR',
  'Which bookings still need a driver?',
  'What did KiwiTaxi email us last week?',
  'Who edited booking #abc123 recently?',
];

/** How many questions the launcher shows at once, page's first. */
export const MAX_SUGGESTIONS = 3;

const AskAiSuggestionContext = createContext(null);

/**
 * Normalises what a page passed into the one shape the launcher renders, or
 * null for "nothing to offer".
 *
 * The key order here is fixed and the result is flat, which is what makes
 * `JSON.stringify` a sound value-equality test in the hook below. Do not add a
 * field whose value is an object, a function or a React element without
 * replacing that comparison.
 */
function normalise(suggestion) {
  if (!suggestion) return null;
  const given = typeof suggestion === 'string' ? { question: suggestion } : suggestion;
  if (!given.question) return null;
  return {
    question: given.question,
    prompt: given.prompt || given.question,
    label: given.label || null,
  };
}

/**
 * Holds the registrations. Wrapped around the dashboard shell in Routes.jsx so
 * that both the pages (which push) and the ChatWidget (which reads) are under
 * it — that is the only wiring this feature needs, and adding a page's
 * suggestion never touches it.
 */
export function AskAiSuggestionProvider({ children }) {
  const [stack, setStack] = useState([]);
  const value = useMemo(() => ({ stack, setStack }), [stack]);
  return (
    <AskAiSuggestionContext.Provider value={value}>{children}</AskAiSuggestionContext.Provider>
  );
}

/**
 * Read side, for the launcher: the live registration, or null when no page has
 * one. Top of the stack — see "IF TWO THINGS REGISTER" above.
 */
export function useActiveAskAiSuggestion() {
  const held = useContext(AskAiSuggestionContext);
  const stack = held?.stack;
  return stack && stack.length ? stack[stack.length - 1] : null;
}

/**
 * Declare the one most relevant thing to ask about THIS page, for as long as it
 * is on screen. The whole contract is in this file's header; the short version
 * is: one per page, optional, inline object fine, falsy means "not yet".
 *
 *   useAskAiSuggestion({
 *     question: `Which of ${day}'s trips still have no driver?`,
 *     prompt: `List the trips on ${day} that have no driver assigned …`,
 *     label: 'Ask about today',
 *   });
 */
export default function useAskAiSuggestion(suggestion) {
  const held = useContext(AskAiSuggestionContext);
  const setStack = held?.setStack;

  // Serialised, so a page can hand us a fresh object literal every render — the
  // ergonomic the whole design rests on — without re-registering every render.
  const entry = normalise(suggestion);
  const serialised = entry ? JSON.stringify(entry) : '';

  useEffect(() => {
    if (!setStack || !serialised) return undefined;
    // Parsed inside the effect so `mine` is created once per registration and
    // its identity is what the cleanup removes. Nothing else can be: two pages
    // may legitimately register the same text.
    const mine = JSON.parse(serialised);
    setStack((live) => [...live, mine]);
    return () => setStack((live) => live.filter((e) => e !== mine));
  }, [setStack, serialised]);
}
